import { create } from 'zustand';
import { User } from '@supabase/supabase-js';
import {
    supabase,
    toSchedule, fromSchedule,
    toClient, fromClient,
    toFeedback, fromFeedback,
    toInventoryItem, fromInventoryItem,
    toScheduleNote, fromScheduleNote,
} from '../lib/supabase';
import { Schedule, Machine, Client, Feedback, InventoryItem, Profile, ScheduleNote } from '../types';

const today = new Date().toLocaleDateString('en-CA');

interface AppState {
    // 인증
    user: User | null;
    isAuthLoading: boolean;
    setUser: (user: User | null) => void;
    setAuthLoading: (v: boolean) => void;
    signIn: (email: string, password: string) => Promise<string | null>;
    signUp: (name: string, phone: string, password: string) => Promise<string | null>;
    signOut: () => Promise<void>;

    // 프로필
    profiles: Profile[];
    loadProfiles: () => Promise<void>;
    updateProfile: (id: string, name: string, phone: string) => Promise<void>;

    // 데이터
    schedules: Schedule[];
    machines: Machine[];
    clients: Client[];
    feedbacks: Feedback[];
    dailyMemos: Record<string, string>;
    inventoryItems: InventoryItem[];

    // UI 상태
    selectedDate: string;
    selectedScheduleId: string | null;
    activeTab: string;

    // 데이터 로딩
    loadAllData: () => Promise<void>;

    // UI 액션
    setSelectedDate: (date: string) => void;
    setSelectedSchedule: (id: string | null) => void;
    setActiveTab: (tab: string) => void;

    // 일정 CRUD
    addSchedule: (schedule: Schedule) => Promise<void>;
    updateSchedule: (id: string, updates: Partial<Schedule>) => Promise<void>;
    deleteSchedule: (id: string) => Promise<void>;
    addFeedback: (feedback: Feedback) => Promise<void>;
    updateDailyMemo: (date: string, memo: string) => Promise<void>;

    // 거래처 CRUD
    addClient: (client: Client) => Promise<void>;
    updateClient: (id: string, updates: Partial<Client>) => Promise<void>;
    favoriteClientIds: string[];
    toggleClientFavorite: (id: string) => Promise<void>;

    // 재고 CRUD
    addInventoryItem: (item: InventoryItem) => Promise<void>;
    updateInventoryItem: (id: string, updates: Partial<InventoryItem>) => Promise<void>;
    deleteInventoryItem: (id: string) => Promise<void>;
    adjustInventoryQuantity: (id: string, delta: number) => Promise<void>;

    // 작업 메모 CRUD
    scheduleNotes: ScheduleNote[];
    addScheduleNote: (note: ScheduleNote) => Promise<void>;
    updateScheduleNote: (id: string, content: string) => Promise<void>;
    deleteScheduleNote: (id: string) => Promise<void>;

    // 조회
    getSchedulesByDate: (date: string) => Schedule[];
    getMachineById: (id: string) => Machine | undefined;
    getClientById: (id: string) => Client | undefined;
    getFeedbackByScheduleId: (scheduleId: string) => Feedback | undefined;
}

export const useStore = create<AppState>((set, get) => ({
    // ─── 인증 ───────────────────────────────────────────
    user: null,
    isAuthLoading: true,

    setUser: (user) => set({ user }),
    setAuthLoading: (v) => set({ isAuthLoading: v }),

    signIn: async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return error ? error.message : null;
    },

    signUp: async (name, phone, password) => {
        const digits = phone.replace(/\D/g, '');
        const email = `${digits}@beomyang.local`;
        const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { name, phone: digits } } });
        if (error) return error.message;
        if (data.user && data.session) set({ user: data.user });
        return null;
    },

    signOut: async () => {
        await supabase.auth.signOut();
        set({
            user: null,
            schedules: [],
            clients: [],
            feedbacks: [],
            dailyMemos: {},
            inventoryItems: [],
            profiles: [],
            scheduleNotes: [],
            favoriteClientIds: [],
        });
    },

    // ─── 데이터 ──────────────────────────────────────────
    schedules: [],
    machines: [],
    clients: [],
    feedbacks: [],
    dailyMemos: {},
    inventoryItems: [],
    profiles: [],
    scheduleNotes: [],
    favoriteClientIds: [],

    // ─── UI 상태 ─────────────────────────────────────────
    selectedDate: today,
    selectedScheduleId: null,
    activeTab: 'schedule',

    // ─── 데이터 로딩 ─────────────────────────────────────
    loadProfiles: async () => {
        const { data } = await supabase.from('profiles').select('*').order('name');
        set({ profiles: (data || []).map((r: any) => ({ id: r.id, name: r.name, phone: r.phone })) });
    },

    updateProfile: async (id, name, phone) => {
        const digits = phone.replace(/\D/g, '');
        const prev = get().profiles.find((p) => p.id === id);
        set((s) => ({
            profiles: s.profiles.map((p) => p.id === id ? { ...p, name, phone: digits } : p),
        }));
        const { error } = await supabase.from('profiles').update({ name, phone: digits }).eq('id', id);
        if (error && prev) {
            set((s) => ({ profiles: s.profiles.map((p) => p.id === id ? prev : p) }));
            alert('저장 오류: ' + error.message);
        }
    },

    loadAllData: async () => {
        const [
            { data: schedules },
            { data: clients },
            { data: feedbacks },
            { data: memos },
            { data: inventory },
            { data: profilesData },
            { data: notes },
            { data: favorites },
        ] = await Promise.all([
            supabase.from('schedules').select('*').order('date', { ascending: false }),
            supabase.from('clients').select('*').order('name', { ascending: true }),
            supabase.from('feedbacks').select('*'),
            supabase.from('daily_memos').select('*'),
            supabase.from('inventory_items').select('*').order('category', { ascending: true }),
            supabase.from('profiles').select('*').order('name'),
            supabase.from('schedule_notes').select('*').order('created_at', { ascending: true }),
            supabase.from('client_favorites').select('client_id'),
        ]);

        const memoMap: Record<string, string> = {};
        (memos || []).forEach((m: any) => { memoMap[m.date] = m.memo; });

        set({
            schedules: (schedules || []).map(toSchedule),
            clients: (clients || []).map(toClient),
            feedbacks: (feedbacks || []).map(toFeedback),
            dailyMemos: memoMap,
            inventoryItems: (inventory || []).map(toInventoryItem),
            profiles: (profilesData || []).map((r: any) => ({ id: r.id, name: r.name, phone: r.phone })),
            scheduleNotes: (notes || []).map(toScheduleNote),
            favoriteClientIds: (favorites || []).map((f: any) => f.client_id),
        });
    },

    // ─── UI 액션 ─────────────────────────────────────────
    setSelectedDate: (date) => set({ selectedDate: date }),
    setSelectedSchedule: (id) => set({ selectedScheduleId: id }),
    setActiveTab: (tab) => set({ activeTab: tab }),

    // ─── 일정 CRUD ───────────────────────────────────────
    addSchedule: async (schedule) => {
        set((s) => ({ schedules: [...s.schedules, schedule] }));
        const { error } = await supabase.from('schedules').insert(fromSchedule(schedule));
        if (error) {
            set((s) => ({ schedules: s.schedules.filter((x) => x.id !== schedule.id) }));
            alert('저장 오류: ' + error.message);
        }
    },

    updateSchedule: async (id, updates) => {
        const prev = get().schedules.find((s) => s.id === id);
        set((s) => ({
            schedules: s.schedules.map((x) => x.id === id ? { ...x, ...updates } : x),
        }));
        const updated = get().schedules.find((s) => s.id === id)!;
        const { error } = await supabase.from('schedules').update(fromSchedule(updated)).eq('id', id);
        if (error && prev) {
            set((s) => ({ schedules: s.schedules.map((x) => x.id === id ? prev : x) }));
            alert('저장 오류: ' + error.message);
        }
    },

    deleteSchedule: async (id) => {
        const prev = get().schedules.find((s) => s.id === id);
        set((s) => ({ schedules: s.schedules.filter((x) => x.id !== id) }));
        const { error } = await supabase.from('schedules').delete().eq('id', id);
        if (error && prev) {
            set((s) => ({ schedules: [...s.schedules, prev] }));
            alert('삭제 오류: ' + error.message);
        }
    },

    addFeedback: async (feedback) => {
        set((s) => ({
            feedbacks: [...s.feedbacks, feedback],
            schedules: s.schedules.map((x) =>
                x.id === feedback.scheduleId ? { ...x, status: '완료' as const } : x
            ),
        }));
        const { error: fbErr } = await supabase.from('feedbacks').insert(fromFeedback(feedback));
        if (!fbErr) {
            await supabase.from('schedules').update({ status: '완료' }).eq('id', feedback.scheduleId);
        }
        if (fbErr) alert('피드백 저장 오류: ' + fbErr.message);
    },

    updateDailyMemo: async (date, memo) => {
        set((s) => ({ dailyMemos: { ...s.dailyMemos, [date]: memo } }));
        const { error } = await supabase
            .from('daily_memos')
            .upsert({ date, memo, updated_at: new Date().toISOString() });
        if (error) console.error('메모 저장 오류:', error.message);
    },

    // ─── 거래처 CRUD ─────────────────────────────────────
    addClient: async (client) => {
        set((s) => ({ clients: [...s.clients, client] }));
        const { error } = await supabase.from('clients').insert(fromClient(client));
        if (error) {
            set((s) => ({ clients: s.clients.filter((x) => x.id !== client.id) }));
            alert('저장 오류: ' + error.message);
        }
    },

    updateClient: async (id, updates) => {
        const prev = get().clients.find((c) => c.id === id);
        set((s) => ({
            clients: s.clients.map((x) => x.id === id ? { ...x, ...updates } : x),
        }));
        const updated = get().clients.find((c) => c.id === id)!;
        const { error } = await supabase.from('clients').update(fromClient(updated)).eq('id', id);
        if (error && prev) {
            set((s) => ({ clients: s.clients.map((x) => x.id === id ? prev : x) }));
            alert('저장 오류: ' + error.message);
        }
    },

    toggleClientFavorite: async (id) => {
        const isFav = get().favoriteClientIds.includes(id);
        if (isFav) {
            set((s) => ({ favoriteClientIds: s.favoriteClientIds.filter((x) => x !== id) }));
            await supabase.from('client_favorites').delete().eq('client_id', id);
        } else {
            set((s) => ({ favoriteClientIds: [...s.favoriteClientIds, id] }));
            const userId = get().user?.id;
            if (userId) await supabase.from('client_favorites').insert({ user_id: userId, client_id: id });
        }
    },

    // ─── 재고 CRUD ───────────────────────────────────────
    addInventoryItem: async (item) => {
        set((s) => ({ inventoryItems: [...s.inventoryItems, item] }));
        const { error } = await supabase.from('inventory_items').insert(fromInventoryItem(item));
        if (error) {
            set((s) => ({ inventoryItems: s.inventoryItems.filter((x) => x.id !== item.id) }));
            alert('저장 오류: ' + error.message);
        }
    },

    updateInventoryItem: async (id, updates) => {
        const prev = get().inventoryItems.find((x) => x.id === id);
        set((s) => ({
            inventoryItems: s.inventoryItems.map((x) => x.id === id ? { ...x, ...updates } : x),
        }));
        const updated = get().inventoryItems.find((x) => x.id === id)!;
        const { error } = await supabase.from('inventory_items').update(fromInventoryItem(updated)).eq('id', id);
        if (error && prev) {
            set((s) => ({ inventoryItems: s.inventoryItems.map((x) => x.id === id ? prev : x) }));
            alert('저장 오류: ' + error.message);
        }
    },

    deleteInventoryItem: async (id) => {
        const prev = get().inventoryItems.find((x) => x.id === id);
        set((s) => ({ inventoryItems: s.inventoryItems.filter((x) => x.id !== id) }));
        const { error } = await supabase.from('inventory_items').delete().eq('id', id);
        if (error && prev) {
            set((s) => ({ inventoryItems: [...s.inventoryItems, prev] }));
            alert('삭제 오류: ' + error.message);
        }
    },

    adjustInventoryQuantity: async (id, delta) => {
        const item = get().inventoryItems.find((x) => x.id === id);
        if (!item) return;
        const newQty = Math.max(0, item.quantity + delta);
        set((s) => ({
            inventoryItems: s.inventoryItems.map((x) => x.id === id ? { ...x, quantity: newQty } : x),
        }));
        const { error } = await supabase
            .from('inventory_items')
            .update({ quantity: newQty, updated_at: new Date().toISOString() })
            .eq('id', id);
        if (error) console.error('수량 저장 오류:', error.message);
    },

    // ─── 작업 메모 CRUD ──────────────────────────────────
    addScheduleNote: async (note) => {
        set((s) => ({ scheduleNotes: [...s.scheduleNotes, note] }));
        const { error } = await supabase.from('schedule_notes').insert(fromScheduleNote(note));
        if (error) {
            set((s) => ({ scheduleNotes: s.scheduleNotes.filter((n) => n.id !== note.id) }));
            alert('메모 저장 오류: ' + error.message);
        }
    },

    updateScheduleNote: async (id, content) => {
        const now = new Date().toISOString();
        set((s) => ({
            scheduleNotes: s.scheduleNotes.map((n) => n.id === id ? { ...n, content, updatedAt: now } : n),
        }));
        const { error } = await supabase.from('schedule_notes').update({ content, updated_at: now }).eq('id', id);
        if (error) console.error('메모 수정 오류:', error.message);
    },

    deleteScheduleNote: async (id) => {
        const prev = get().scheduleNotes.find((n) => n.id === id);
        set((s) => ({ scheduleNotes: s.scheduleNotes.filter((n) => n.id !== id) }));
        const { error } = await supabase.from('schedule_notes').delete().eq('id', id);
        if (error && prev) {
            set((s) => ({ scheduleNotes: [...s.scheduleNotes, prev] }));
            alert('메모 삭제 오류: ' + error.message);
        }
    },

    // ─── 조회 ────────────────────────────────────────────
    getSchedulesByDate: (date) => get().schedules.filter((s) => s.date === date),
    getMachineById: (id) => get().machines.find((m) => m.id === id),
    getClientById: (id) => get().clients.find((c) => c.id === id),
    getFeedbackByScheduleId: (scheduleId) => get().feedbacks.find((f) => f.scheduleId === scheduleId),
}));
